import { Link, createFileRoute } from '@tanstack/react-router'

import { PageBackButton } from '@/components/common/PageBackButton'
import { Badge } from '@/components/ui/badge'
import { OrderStatusBadge } from '@/components/sections/order-status-badge'
import { ContractDeliveryStatusBadge } from '@/components/contract-delivery/contract-delivery-status-badge'
import { TripOrderActionsCell } from '@/components/delivery/trip-order-actions'
import { TripOrderCard } from '@/components/delivery/trip-order-card'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useDeliveryTrip } from '@/hooks/use-delivery-trips-query'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/delivery-trips/$id',
)({
    component: DeliveryTripDetailPage,
})

function formatDuration(start: string, end?: string | null) {
    const startMs = new Date(start).getTime()
    const endMs = end ? new Date(end).getTime() : Date.now()
    const diff = Math.max(0, endMs - startMs)
    const hours = Math.floor(diff / 3_600_000)
    const minutes = Math.floor((diff % 3_600_000) / 60_000)
    return `${hours}h ${minutes}m`
}

function DeliveryTripDetailPage() {
    const { id } = Route.useParams()
    const { data: trip, isLoading } = useDeliveryTrip(id)

    if (isLoading) {
        return (
            <p className="text-sm text-muted-foreground">
                {m.common_loading()}
            </p>
        )
    }
    if (!trip) {
        return (
            <p className="text-sm text-muted-foreground">
                {m.trip_error_no_active()}
            </p>
        )
    }

    const orders = trip.orders ?? []
    const contractOrders = trip.contract_orders ?? []
    const totalStops = orders.length + contractOrders.length

    return (
        <div className="space-y-4 page-pad">
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <div className="flex min-w-0 items-center gap-2">
                        <PageBackButton className="-my-2 md:-my-1" />
                        <CardTitle className="font-mono">
                            {trip.trip_code}
                        </CardTitle>
                    </div>
                    <Badge
                        variant={
                            trip.status === 'active' ? 'success' : 'secondary'
                        }
                    >
                        {trip.status === 'active'
                            ? m.trip_status_active()
                            : m.trip_status_closed()}
                    </Badge>
                </CardHeader>
                <CardContent>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                        <div>
                            <dt className="text-xs uppercase text-muted-foreground">
                                {m.trip_driver()}
                            </dt>
                            <dd>{trip.driver_name ?? '—'}</dd>
                        </div>
                        <div>
                            <dt className="text-xs uppercase text-muted-foreground">
                                {m.trip_vehicle()}
                            </dt>
                            <dd>
                                {trip.vehicle
                                    ? `${trip.vehicle.plate_number} — ${trip.vehicle.name}`
                                    : '—'}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-xs uppercase text-muted-foreground">
                                {m.trip_started_at()}
                            </dt>
                            <dd>
                                {new Date(trip.started_at).toLocaleString()}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-xs uppercase text-muted-foreground">
                                {m.trip_ended_at()}
                            </dt>
                            <dd>
                                {trip.ended_at
                                    ? new Date(trip.ended_at).toLocaleString()
                                    : m.trip_running_now()}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-xs uppercase text-muted-foreground">
                                {m.trip_duration()}
                            </dt>
                            <dd>
                                {formatDuration(trip.started_at, trip.ended_at)}
                            </dd>
                        </div>
                        {trip.note && (
                            <div className="col-span-2">
                                <dt className="text-xs uppercase text-muted-foreground">
                                    {m.common_notes()}
                                </dt>
                                <dd>{trip.note}</dd>
                            </div>
                        )}
                    </dl>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>
                        {m.trip_orders_in_trip()} ({totalStops})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {totalStops === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            {m.trip_no_orders()}
                        </p>
                    ) : (
                        <>
                            {/* Mobile: mỗi đơn là 1 card, tap-friendly */}
                            <div className="space-y-2 md:hidden">
                                {orders.map((o) => (
                                    <TripOrderCard
                                        key={o.id}
                                        kind="sale"
                                        order={o}
                                        tripId={id}
                                    />
                                ))}
                                {contractOrders.map((o) => (
                                    <TripOrderCard
                                        key={o.id}
                                        kind="contract"
                                        order={o}
                                        tripId={id}
                                    />
                                ))}
                            </div>

                            {/* Desktop: bảng */}
                            <div className="hidden md:block">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>
                                                {m.order_code()}
                                            </TableHead>
                                            <TableHead>
                                                {m.customer_name_label()}
                                            </TableHead>
                                            <TableHead>
                                                {m.common_status_label()}
                                            </TableHead>
                                            <TableHead className="text-right">
                                                {m.order_total()}
                                            </TableHead>
                                            <TableHead className="text-right">
                                                {m.common_actions()}
                                            </TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {orders.map((o) => (
                                            <TableRow key={o.id}>
                                                <TableCell className="font-medium">
                                                    <Link
                                                        to="/orders/$orderId"
                                                        params={{
                                                            orderId: o.id,
                                                        }}
                                                        className="text-primary hover:underline"
                                                    >
                                                        {o.order_code}
                                                    </Link>
                                                </TableCell>
                                                <TableCell>
                                                    {o.customer_name}
                                                </TableCell>
                                                <TableCell>
                                                    <OrderStatusBadge
                                                        status={o.status}
                                                    />
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {o.total_amount.toLocaleString()}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <TripOrderActionsCell
                                                        orderId={o.id}
                                                        status={o.status}
                                                        driverAssigneeId={
                                                            o.driver_assignee_id
                                                        }
                                                        kind="sale"
                                                        tripId={id}
                                                        order={o}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {contractOrders.map((o) => (
                                            <TableRow key={o.id}>
                                                <TableCell className="font-medium">
                                                    <Link
                                                        to="/contract-deliveries/$id"
                                                        params={{ id: o.id }}
                                                        className="text-primary hover:underline"
                                                    >
                                                        {o.order_code}
                                                    </Link>
                                                </TableCell>
                                                <TableCell>
                                                    {o.recipient_name}
                                                    <span className="ml-1 text-xs text-muted-foreground">
                                                        {m.cdo_contract_tag()}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <ContractDeliveryStatusBadge
                                                        status={o.status}
                                                    />
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    —
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <TripOrderActionsCell
                                                        orderId={o.id}
                                                        status={o.status}
                                                        driverAssigneeId={
                                                            o.driver_assignee_id
                                                        }
                                                        kind="contract"
                                                        tripId={id}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}
